import React, { MutableRefObject, useCallback, useMemo, useRef, useState } from 'react';
import * as ReactDOM from 'react-dom/client';
import { ConfigIconProvider, Icon } from '@fuxi/eevee-icon';
import { ScreenOrientation, OrientationType } from '@capawesome/capacitor-screen-orientation';
import { CameraPosition, CameraPreview } from '@capacitor-my-custom/camera-preview';
import { useMount } from 'ahooks';
import { throttle } from 'lodash-es';
import { showWholePage } from '@/hooks/useScanQRCode';
import { base64String2ObjectURL, uploadBase64Img } from '@/utils';
import { CAMERA_MARKER_DOM_ID, CustomCameraEventBus } from '@/hooks/useCustomCamera';
import { ICON_CDN_LINK } from '@/constant';

import cx from './index.module.scss';

interface Props {
  rootRef: MutableRefObject<ReactDOM.Root>;
  markerConfig: {
    url: string;
    width: string | number;
  };
  buttonPosition: 'left' | 'right';
  shouldTransFromFrontImage: boolean;
}

// 因前置摄像头的图片是镜像且旋转了90度的，所以需要对图片进行变化处理
const transformBase64Image = (url: string) => {
  return new Promise<string>((resolve, reject) => {
    // 创建一个 Image 对象
    let img = new Image();

    img.src = url;

    img.onload = () => {
      // 创建一个 Canvas 元素
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      // 设置 Canvas 的宽高为图片的高宽（因为旋转90度后宽高互换）
      canvas.width = img.height;
      canvas.height = img.width;

      // 将 Canvas 旋转 90 度并沿 Y 轴翻转
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate(-Math.PI / 2);
      ctx.scale(1, -1); // 沿 Y 轴翻转
      ctx.drawImage(img, -img.width / 2, -img.height / 2);

      // 获取旋转后的图片的 base64 编码
      const rotatedBase64Image = canvas.toDataURL('image/png', 1.0);
      URL.revokeObjectURL(url);
      img.src = '';
      img = null;
      resolve(rotatedBase64Image);
    };

    img.onerror = error => {
      reject(error);
    };
  });
};
// 因前置摄像头的图片是镜像且倒置，所以需要对图片进行变化处理 横向
const transformBase64ImageHorizontal = (url: string) => {
  return new Promise<string>((resolve, reject) => {
    // 创建一个 Image 对象
    let img = new Image();

    img.src = url;

    img.onload = () => {
      // 创建一个 Canvas 元素
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      // 第一步：处理设备旋转 (示例为90度顺时针旋转)
      canvas.width = img.width;
      canvas.height = img.height;

      // 设置 Canvas 尺寸与图片一致
      canvas.width = img.width;
      canvas.height = img.height;

      // 将坐标系原点移至 Canvas 中心
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.scale(1, -1); // 沿 Y 轴翻转
      ctx.drawImage(img, -img.width / 2, -img.height / 2);

      // 获取旋转后的图片的 base64 编码
      const rotatedBase64Image = canvas.toDataURL('image/png', 1.0);
      URL.revokeObjectURL(url);
      img.src = '';
      img = null;
      resolve(rotatedBase64Image);
    };

    img.onerror = error => {
      reject(error);
    };
  });
};

const CLICK_THROTTLE_TIME = 500;
const MAIN_ICNO_SIZE = 80;
const SUB_ICON_SIZE = 60;

const CameraMarker: React.FC<Props> = ({
  rootRef,
  markerConfig,
  shouldTransFromFrontImage,
  buttonPosition = 'right',
}) => {
  const [imgUrl, setImgUrl] = useState('');
  const [currentScreenOrientation, setCurrentScreenOrientation] = useState<OrientationType>();
  const currentCameraPosition = useRef<CameraPosition>('rear');

  // 移除相机 marker DOM
  const removeMarkerDom = useCallback(() => {
    setTimeout(() => {
      rootRef.current.unmount();
      const container = document.getElementById(CAMERA_MARKER_DOM_ID);
      container && document.body.removeChild(container);
    }, 0);
  }, [rootRef]);

  // 点击拍摄
  const handleSnap = useMemo(
    () =>
      throttle(async () => {
        let base64ImgString = await CameraPreview.capture({ quality: 100 });

        // 前置摄像头的图片需要进行变换处理
        if (currentCameraPosition.current === 'front') {
          const imgUrl = base64String2ObjectURL(base64ImgString?.value);
          let newBase64ImgString = shouldTransFromFrontImage
            ? await transformBase64Image(imgUrl)
            : await transformBase64ImageHorizontal(imgUrl);

          const newImgUrl = base64String2ObjectURL(newBase64ImgString);

          newBase64ImgString = null;
          setImgUrl(newImgUrl);
        } else {
          const imgUrl = base64String2ObjectURL(base64ImgString?.value);

          setImgUrl(imgUrl);
        }

        base64ImgString = null;
      }, CLICK_THROTTLE_TIME),
    [shouldTransFromFrontImage]
  );

  // 确认拍摄的图片
  const confirmSnapedImg = useMemo(
    () =>
      throttle(async () => {
        await CameraPreview.stop();
        removeMarkerDom();
        showWholePage();

        let url: string;

        try {
          url = await uploadBase64Img(imgUrl, true);
          URL.revokeObjectURL(imgUrl);
        } finally {
          CustomCameraEventBus.emit('cameraShootResult', url);
        }
      }, CLICK_THROTTLE_TIME),
    [imgUrl, removeMarkerDom]
  );

  // 重拍
  const handleReSnap = useMemo(
    () =>
      throttle(async () => {
        imgUrl && URL.revokeObjectURL(imgUrl);
        setImgUrl('');
      }, CLICK_THROTTLE_TIME),
    [imgUrl]
  );

  // 取消拍摄
  const handleCancelSnap = useMemo(
    () =>
      throttle(async () => {
        removeMarkerDom();
        await CameraPreview.stop();
        showWholePage();
      }, CLICK_THROTTLE_TIME),
    [removeMarkerDom]
  );

  // 翻转摄像头
  const tranverseCamera = useMemo(
    () =>
      throttle(() => {
        CameraPreview.flip().then(() => {
          currentCameraPosition.current = currentCameraPosition.current === 'rear' ? 'front' : 'rear';
        });
      }, CLICK_THROTTLE_TIME),
    []
  );

  const init = useCallback(async () => {
    const orientation = await ScreenOrientation.getCurrentOrientation();
    setCurrentScreenOrientation(orientation?.type);
  }, []);

  /** 根据当前分辨率动态计算按钮大小 */
  const iconSize = useMemo(
    () =>
      currentScreenOrientation === OrientationType.LANDSCAPE_PRIMARY
        ? {
            mainIcon: ((MAIN_ICNO_SIZE / 1300) * 100).toFixed(4) + 'vw',
            subIcon: ((SUB_ICON_SIZE / 1300) * 100).toFixed(4) + 'vw',
          }
        : {
            mainIcon: ((MAIN_ICNO_SIZE / 780) * 100).toFixed(4) + 'vw',
            subIcon: ((SUB_ICON_SIZE / 780) * 100).toFixed(4) + 'vw',
          },
    [currentScreenOrientation]
  );

  useMount(init);

  return (
    <ConfigIconProvider cdnLink={ICON_CDN_LINK}>
      <div className={cx('CameraMarker')}>
        {!!imgUrl && (
          <div className={cx('img-preview')}>
            <img src={imgUrl} alt="" />
          </div>
        )}
        {!!(markerConfig?.url && !imgUrl) && (
          <div className={cx('camera-marker')}>
            <img
              src={markerConfig.url}
              style={{ ...(markerConfig?.width ? { width: markerConfig.width } : {}) }}
              alt=""
            />
          </div>
        )}
        <div
          className={cx('opearte-btns', buttonPosition)}
          style={{ '--main-icon-size': iconSize.mainIcon, '--sub-icon-size': iconSize.subIcon } as any}
        >
          {imgUrl ? (
            <>
              <Icon name="back-gray" className={cx('icon')} onClick={handleReSnap} />
              <Icon name="success-white" className={cx('icon', 'success-confirm')} onClick={confirmSnapedImg} />
            </>
          ) : (
            <>
              <Icon name="back-gray" className={cx('icon')} onClick={handleCancelSnap} />
              <Icon name="camera-snap" className={cx('icon', 'camera-snap')} onClick={handleSnap} />
              <Icon name="reset-gray" className={cx('icon')} onClick={tranverseCamera} />
            </>
          )}
        </div>
      </div>
    </ConfigIconProvider>
  );
};

export default CameraMarker;
